import React from 'react';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { useGrow } from '../../orbit-room/OverviewCharts';

export const EASE = [0.22, 1, 0.36, 1] as const;

export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

const DAY = 86400000;

export const ageDays = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.floor((Date.now() - at) / DAY));
};

/* Every card on the dashboard enters the same way, a beat after the one before. */
export const Reveal: React.FC<{ order?: number; className?: string; children: React.ReactNode }> = ({ order = 0, className = '', children }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(order, 10) * 0.05, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

export const Card: React.FC<{
  title: string;
  sub?: string;
  action?: React.ReactNode;
  id?: string;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ title, sub, action, id, className = '', bodyClassName = '', children }) => (
  <section
    id={id}
    aria-label={title}
    className={clsx('flex h-full min-h-0 scroll-mt-24 flex-col rounded-[14px] border border-line bg-white shadow-[0_1px_2px_rgba(24,25,29,0.04)] transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(24,25,29,0.06)]', className)}
  >
    <header className="flex items-start justify-between gap-3 px-5 pt-4">
      <div className="min-w-0">
        <h2 className="m-0 text-[13.5px] font-semibold leading-5 text-ink">{title}</h2>
        {sub && <p className="m-0 mt-0.5 text-[12px] leading-[17px] text-ink-dim">{sub}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
    <div className={clsx('flex flex-1 flex-col px-5 pb-5 pt-4', bodyClassName)}>{children}</div>
  </section>
);

export const CardLink: React.FC<{ onClick: () => void; children: React.ReactNode; label?: string }> = ({ onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[12px] font-semibold text-ink-muted ring-1 ring-inset ring-line transition-colors duration-200 hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
  >
    {children}
  </button>
);

export const Legend: React.FC<{ items: { label: string; tone: string; value?: number }[] }> = ({ items }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
    {items.map((i) => (
      <span key={i.label} className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-muted">
        <i className="h-2.5 w-2.5 rounded-[3px] ring-1 ring-inset ring-black/10" style={{ background: i.tone }} />
        {i.label}
        {i.value !== undefined && <b className="font-semibold tabular-nums text-ink">{i.value}</b>}
      </span>
    ))}
  </div>
);

export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden className={clsx('animate-pulse rounded-lg bg-canvas-inset', className)} />
);

export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 220 }) => (
  <div aria-busy="true" className="rounded-[14px] border border-line bg-white p-5" style={{ height }}>
    <Skeleton className="h-4 w-32" />
    <Skeleton className="mt-2 h-3 w-48" />
    <Skeleton className="mt-6 h-[calc(100%-64px)] w-full" />
  </div>
);

/* A small trend line for a KPI. Points are counts, drawn left to right. */
export const Sparkline: React.FC<{ points: number[]; tone?: string; label: string }> = ({ points, tone = '#18191D', label }) => {
  const t = useGrow(1.1);
  const w = 88;
  const h = 30;
  const max = Math.max(1, ...points);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const coords = points.map((p, i) => [i * step, h - 3 - ((p / max) * (h - 6))]);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const last = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-[30px] w-[88px] overflow-visible" role="img" aria-label={label}>
      <defs>
        <clipPath id={`spark-${label.replace(/\W+/g, '')}`}>
          <rect x="0" y="-4" width={w * t} height={h + 8} />
        </clipPath>
      </defs>
      <g clipPath={`url(#spark-${label.replace(/\W+/g, '')})`}>
        <path d={area} fill={tone} opacity={0.08} />
        <path d={line} fill="none" stroke={tone} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {last && t > 0.98 && <circle cx={last[0]} cy={last[1]} r={2.75} fill={tone} stroke="#fff" strokeWidth={1.5} />}
    </svg>
  );
};

/* A short two-or-three part bar for a KPI, each part named for screen readers. */
export const MiniBar: React.FC<{ parts: { value: number; tone: string; label: string }[] }> = ({ parts }) => {
  const t = useGrow(1);
  const total = Math.max(1, parts.reduce((s, p) => s + p.value, 0));
  return (
    <span className="flex h-2 w-[88px] overflow-hidden rounded-full bg-canvas-inset" role="img" aria-label={parts.map((p) => `${p.label} ${p.value}`).join(', ')}>
      {parts.filter((p) => p.value > 0).map((p) => (
        <span key={p.label} className="h-full border-r-2 border-white last:border-r-0" style={{ width: `${(p.value / total) * 100 * t}%`, background: p.tone }} title={`${p.label}: ${p.value}`} />
      ))}
    </span>
  );
};

export const MiniRing: React.FC<{ value: number; label: string; tone?: string; size?: number }> = ({ value, label, tone = '#FFEA00', size = 34 }) => {
  const t = useGrow(1);
  const r = 13;
  const c = 2 * Math.PI * r;
  const share = Math.min(1, Math.max(0, value));
  return (
    <svg viewBox="0 0 34 34" style={{ width: size, height: size }} className="-rotate-90" role="img" aria-label={label}>
      <circle cx="17" cy="17" r={r} fill="none" stroke="#F3F3F1" strokeWidth="4.5" />
      <circle cx="17" cy="17" r={r} fill="none" stroke={tone} strokeWidth="4.5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - share * t)} />
    </svg>
  );
};

export const SectionLabel: React.FC<{ title: string; sub?: string; action?: React.ReactNode }> = ({ title, sub, action }) => (
  <div className="mb-3 mt-2 flex items-end justify-between gap-3">
    <div>
      <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">{title}</p>
      {sub && <p className="m-0 mt-1 text-[12.5px] text-ink-dim">{sub}</p>}
    </div>
    {action}
  </div>
);
